import { usePromoContext } from 'fitify-ui-onboarding/src/contexts/PromoContext'
import { useNavigation } from 'fitify-ui-onboarding/src/hooks/useNavigation'
import { usePreload } from 'fitify-ui-onboarding/src/hooks/usePreload'
import { useStepValidation } from 'fitify-ui-onboarding/src/hooks/useStepValidation'
import { useMemo } from 'react'

import { CHF01_ROUTES } from 'data/routes/chf01'
import { CHM01_ROUTES } from 'data/routes/chm01'
import { F01_ROUTES } from 'data/routes/f01'
import { GYMF01_ROUTES } from 'data/routes/gymf01'
import { HOPIF01_ROUTES } from 'data/routes/hopif01'
import { M01_ROUTES } from 'data/routes/m01'
import { MILICAF01_ROUTES } from 'data/routes/milicaf01'
import { MILICAM01_ROUTES } from 'data/routes/milicam01'
import { SHARED_ROUTES } from 'data/routes/shared'
import { TCHF01_ROUTES } from 'data/routes/tchf01'
import { TCHM01_ROUTES } from 'data/routes/tchm01'
import { WALLPIF01_ROUTES } from 'data/routes/wallpif01'
import { useFunnel } from 'hooks/useFunnel'
import { type DataState, Funnel, OnboardingVariant } from 'types/state'

import {
  applyDidYouKnowPlacement,
  applyEligibilityPitchPlacement,
} from './useStepNavigation.Utils'

type Props = {
  data: DataState
}

export const useStepNavigation = ({ data }: Props) => {
  const { promoCode } = usePromoContext()
  const funnel = useFunnel()

  const { routes: baseRoutes, funnelData } = useMemo(() => {
    switch (funnel) {
      case Funnel.CHF01:
        return {
          routes: CHF01_ROUTES.pagesMap,
          funnelData: data[Funnel.CHF01],
        }
      case Funnel.CHM01:
        return {
          routes: CHM01_ROUTES.pagesMap,
          funnelData: data[Funnel.CHM01],
        }
      case Funnel.GYMF01:
        return {
          routes: GYMF01_ROUTES.pagesMap,
          funnelData: data[Funnel.GYMF01],
        }
      case Funnel.HOPIF01:
        return {
          routes: HOPIF01_ROUTES.pagesMap,
          funnelData: data[Funnel.HOPIF01],
        }
      case Funnel.MILICAF01:
        return {
          routes: MILICAF01_ROUTES.pagesMap,
          funnelData: data[Funnel.MILICAF01],
        }
      case Funnel.MILICAM01:
        return {
          routes: MILICAM01_ROUTES.pagesMap,
          funnelData: data[Funnel.MILICAM01],
        }
      case Funnel.TCHF01:
        return {
          routes: TCHF01_ROUTES.pagesMap,
          funnelData: data[Funnel.TCHF01],
        }
      case Funnel.TCHM01:
        return {
          routes: TCHM01_ROUTES.pagesMap,
          funnelData: data[Funnel.TCHM01],
        }
      case Funnel.WALLPIF01:
        return {
          routes: WALLPIF01_ROUTES.pagesMap,
          funnelData: data[Funnel.WALLPIF01],
        }
      default:
        break
    }

    const isMixedVariant =
      data.common.onboardingVariant === OnboardingVariant.Mixed

    if (isMixedVariant) {
      switch (funnel) {
        case Funnel.F01:
          return {
            routes: [
              ...SHARED_ROUTES.pagesMap,
              ...F01_ROUTES.pagesMap.filter(
                (route) => route.path !== '/f01/welcome-age-buckets'
              ),
            ],
            funnelData: { ...data.shared, ...data[Funnel.F01] },
          }
        case Funnel.M01:
          return {
            routes: [
              ...SHARED_ROUTES.pagesMap,
              ...M01_ROUTES.pagesMap.filter(
                (route) => route.path !== '/m01/welcome-age-buckets'
              ),
            ],
            funnelData: { ...data.shared, ...data[Funnel.M01] },
          }
        default:
          return { routes: SHARED_ROUTES.pagesMap, funnelData: data.shared }
      }
    }

    switch (funnel) {
      case Funnel.F01:
        return {
          routes: F01_ROUTES.pagesMap,
          funnelData: data[Funnel.F01],
        }
      case Funnel.M01:
        return {
          routes: M01_ROUTES.pagesMap,
          funnelData: data[Funnel.M01],
        }
      default:
        return { routes: SHARED_ROUTES.pagesMap, funnelData: data.shared }
    }
  }, [data, funnel])

  const routes = useMemo(
    () =>
      applyEligibilityPitchPlacement(
        applyDidYouKnowPlacement(baseRoutes, data.common.didYouKnowPlacement),
        data.common.eligibilityPitchVariant,
        funnel
      ),
    [
      baseRoutes,
      data.common.didYouKnowPlacement,
      data.common.eligibilityPitchVariant,
      funnel,
    ]
  )

  const allowedRoutes = useNavigation(routes, promoCode, funnel)

  // Validates previous step
  useStepValidation(allowedRoutes, funnelData, promoCode)
  usePreload()

  return { funnel, funnelData }
}
